import React from 'react';

import { BarList } from './parts';
import { unableReasonText } from './processHistoryLabels';

// 加工场景识别卡 (2026-09-16 从 ProcessHistoryList 抽出共用)。
//
// 两个页面用同一张卡, 但**数据来自两条独立的链路**:
//   历史加工数据 (/assets/process-history)  平台自己的 asset_process_history,
//                                          由 material-asset-labeler 离线打完, 已封存;
//   加工数据中心 (/assets/process-records)  prism 的 material_process_archives,
//                                          由 prism 的 scene_label_service 增量打.
// 两条链路的 VLM 调用并不完全一致 (prism 侧没有 responseSchema、网关与协议也不同),
// 所以**两页的数不该互相验算**, 但呈现口径必须一样 —— 这正是把卡抽出来共用的理由。
//
// **三个分母各不相同, 这是这张卡最容易读错的地方**:
//   八类标签  分母 = 可评估张数。一图平均 2.4 个标签, 八类之和远大于 100% ——
//             所以标题里必须把分母写出来, 否则会被读成"几何占了 90%, 剩下 10% 是别的"。
//   无法评估  分母 = 已打标张数, 单独一行、**不混进八类** —— 混进去就成了九分类。
//   原因      分母 = 无法评估张数。
//
// 「无法评估」不是失败: 三成多的图根本不是成品照 (加工过程记录、设备界面截图),
// 那是数据本身的成分, 不是打标质量问题。

export interface SceneDist {
    labeled: number;
    evaluable: number;
    unevaluable: number;
    /** 8 类场景, 降序。**一图可多标签**, 之和 > evaluable */
    tags: { key: string; count: number }[];
    unableReasons: { key: string; count: number }[];
}

const pct = (n: number, d: number) => (d > 0 ? `${((n / d) * 100).toFixed(1)}%` : '—');

export function SceneCard({ scene, note, onTagClick, onReasonClick }: {
    scene?: SceneDist;
    note?: string;
    /** 点场景条形下钻 (识别页用: 跳到明细只看这个场景) */
    onTagClick?: (tag: string) => void;
    /** 点无法评估原因下钻 (明细只能筛到「不可评估」这一档, 不细到原因) */
    onReasonClick?: (reason: string) => void;
}) {
    if (!scene || scene.labeled === 0) {
        return (
            <div className="card p-4">
                <div className="text-sm font-semibold text-slate-700 mb-1">加工场景识别</div>
                <div className="text-xs text-slate-400">暂无打标数据</div>
            </div>
        );
    }
    const { labeled, evaluable, unevaluable, tags, unableReasons } = scene;
    const avg = evaluable > 0 ? tags.reduce((n, t) => n + t.count, 0) / evaluable : 0;
    return (
        <div className="grid grid-cols-2 gap-4">
            <BarList title="加工场景识别" labelWidth="w-20"
                hint={`可评估 ${evaluable.toLocaleString()} 张 · 平均 ${avg.toFixed(2)} 个标签/张`}
                note={note ?? '基于已加工材料的结果图。一图可多标签，占比之和大于 100%。'}
                rows={tags.map((t) => ({
                    label: t.key, value: t.count,
                    share: pct(t.count, evaluable),
                    onClick: onTagClick ? () => onTagClick(t.key) : undefined,
                }))}
                footer={
                    <div className="border-t border-slate-100 pt-2 text-[11px] text-slate-500">
                        另有 <b>{unevaluable.toLocaleString()}</b> 张无法评估
                        （{pct(unevaluable, labeled)}，不计入上面八类）
                    </div>
                } />
            <BarList title="无法评估的原因" labelWidth="w-24"
                hint={`共 ${unevaluable.toLocaleString()} 张`}
                note="多数是「非成品照」——加工过程记录、设备界面截图。这是数据本身的成分，不是打标质量问题。"
                rows={unableReasons.map((r) => ({
                    label: unableReasonText(r.key), value: r.count,
                    share: pct(r.count, unevaluable),
                    color: 'rgba(148,163,184,.8)',
                    onClick: onReasonClick ? () => onReasonClick(r.key) : undefined,
                }))} />
        </div>
    );
}

export default SceneCard;
